import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFrontChatControl } from '@features/front-chat';
import { PracticeSettingsCatalogLayout } from '@features/organizations/components/PracticeSettings/PracticeSettingsCatalogLayout';
import { useCan } from '@features/user';
import { Alert, Box, Button } from '@mui/material';
import { FeatureGates, useFeatureGate } from '@services/statsig';

import { TemplateCatalogContainer } from './TemplateCatalogContainer';

/**
 * Full-height route for the note-template catalog (GDN-2482): /templates/catalog.
 * A data-dense workbench wants a bounded, full-width viewport, so this route
 * lives OUTSIDE the templates management layout, with a back link to it. The shell supplies the available height so the catalog's flex column
 * can pin its footer below navigation and any dynamic banner.
 *
 * No auto-redirect on a false gate: Statsig may still be initializing on a hard
 * reload, and bouncing a legitimate user is worse than a static fallback with a
 * way back.
 */
export const TemplateCatalogPage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const isEnabled = useFeatureGate(FeatureGates.PRACTICE_TEMPLATE_CATALOG);
  const canManageTemplates = useCan('manage_templates');
  const { hideLauncher, showLauncher } = useFrontChatControl();

  // The Front chat FAB sits bottom-left — exactly over the footer's selection
  // summary. Hide it for the workbench, restore on leave.
  useEffect(() => {
    hideLauncher();
    return (): void => {
      showLauncher();
    };
  }, [hideLauncher, showLauncher]);

  const handleBack = useCallback((): void => {
    navigate(embedded ? '/practice/settings/note-templates' : '/templates');
  }, [navigate, embedded]);

  if (!isEnabled || !canManageTemplates) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleBack}>
              Back
            </Button>
          }
        >
          {canManageTemplates
            ? 'The note-template catalog is not enabled for your practice.'
            : 'You do not have permission to manage note templates.'}
        </Alert>
      </Box>
    );
  }

  return (
    <PracticeSettingsCatalogLayout
      id="template-catalog-workspace"
      embedded={embedded}
      title="Note template catalog"
      subtitle="Browse and manage note templates across your practice."
      backLabel="Note templates"
      onBack={handleBack}
    >
      <TemplateCatalogContainer />
    </PracticeSettingsCatalogLayout>
  );
};

export default TemplateCatalogPage;
